import { useEffect, useState } from 'react';

/**
 * Tiny banner that appears when the browser reports we're offline.
 *
 * Doesn't try to be clever about partial connectivity — just listens to
 * `online` and `offline` events from `window`. Most modern browsers fire
 * these reliably, including when the user puts their machine to sleep.
 *
 * The PWA still works while offline for cached pages, but mutations
 * (submit, approve, etc.) will fail because we don't queue them. Banner
 * is a heads-up, not a blocker — users can keep navigating, just shouldn't
 * try to push changes.
 */
export function OfflineBanner() {
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (online) return null;

  return (
    <div
      role="status"
      className="fixed top-0 left-0 right-0 z-50 bg-warning-subtle text-warning border-b border-warning/30 text-xs font-medium px-4 py-2 text-center"
      // Home-screen app: keep the text clear of the iOS status bar.
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)' }}
    >
      Offline — changes won&rsquo;t save until you reconnect.
    </div>
  );
}
